import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { showsAPI } from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { toArray } from '../utils/apiData';
import { CardSkeleton } from './LoadingSkeletons';

const HorseShowRecords = ({ horseId }) => {
  const [showRecords, setShowRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadShowRecords = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch show records filtered by horse_id
      const response = await showsAPI.getAll({ horse_id: horseId });
      
      // Sort by show date (most recent first)
      const records = toArray(response.data, 'show records');
      records.sort((a, b) => {
        const dateA = new Date(a.show_date);
        const dateB = new Date(b.show_date);
        return dateB - dateA;
      });
      
      setShowRecords(records);
    } catch (error) {
      console.error('Error loading show records:', error);
    } finally {
      setLoading(false);
    }
  }, [horseId]);

  useEffect(() => {
    loadShowRecords();
  }, [loadShowRecords]);

  const displayDate = (value) => formatDate(value) ?? '-';

  const formatCurrency = (value) => {
    if (value == null || value === '') return '-';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value);
  };

  const formatScore = (value) => {
    if (value == null || value === '') return '-';
    return value.toString();
  };

  // Calculate total earnings from show records
  const totalEarnings = showRecords.reduce((sum, record) => {
    const earnings = parseFloat(record.earnings) || 0;
    return sum + earnings;
  }, 0);

  if (loading) {
    return <CardSkeleton label="Loading show records…" />;
  }

  return (
    <Card>
      <CardContent>
        {showRecords.length > 0 && (
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Typography variant="body1" color="textSecondary">
              Total Earnings: {formatCurrency(totalEarnings)}
            </Typography>
          </Box>
        )}

        {showRecords.length === 0 ? (
          <Typography color="textSecondary">No show records found</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Show</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Score</TableCell>
                  <TableCell>Placing</TableCell>
                  <TableCell>Earnings</TableCell>
                  <TableCell>Rider</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {showRecords.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>{displayDate(record.show_date)}</TableCell>
                    <TableCell>{record.event_name || '-'}</TableCell>
                    <TableCell>{record.show_name || '-'}</TableCell>
                    <TableCell>{record.class_name || '-'}</TableCell>
                    <TableCell>{formatScore(record.score)}</TableCell>
                    <TableCell>{record.placing || '-'}</TableCell>
                    <TableCell>{formatCurrency(record.earnings)}</TableCell>
                    <TableCell>{record.rider_name || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default HorseShowRecords;

